import { useEffect, useState } from "react";
import { Navigate, useLocation, useParams } from "react-router-dom";
import { doc, getDoc } from "firebase/firestore";
import AppLoadingScreen from "@/components/AppLoadingScreen";
import { FIRESTORE_COLLECTION_NAME } from "@/config";
import { firestore } from "@/firebase";
import type { VideoState } from "@/types";

/**
 * Keeps old Rapid Review URLs useful without rendering the retired viewer.
 *
 * Existing analyses pass through `/events/...`, where
 * `LegacyAnalysisRedirect` resolves batch membership and preserves the
 * livestream-only viewer. Old "new analysis" links open the batch workflow
 * with their video preselected.
 */
export default function LegacyRapidReviewRedirect() {
  const { videoId, analysisId } = useParams();
  const location = useLocation();
  const prefix = location.pathname.startsWith("/live-demo/")
    ? "/live-demo"
    : "";
  const params = new URLSearchParams(location.search);
  const isNewAnalysis = params.get("newAnalysis") === "true" && !analysisId;
  const [video, setVideo] = useState<VideoState | null>(null);
  const [loadFailed, setLoadFailed] = useState(false);

  useEffect(() => {
    if (!isNewAnalysis || !videoId) return;

    let cancelled = false;
    (async () => {
      try {
        const snapshot = await getDoc(
          doc(firestore, FIRESTORE_COLLECTION_NAME, videoId),
        );
        if (cancelled) return;
        if (!snapshot.exists()) {
          setLoadFailed(true);
          return;
        }
        setVideo({
          ...(snapshot.data() as Omit<VideoState, "id" | "video_id">),
          id: snapshot.id,
          video_id: snapshot.id,
        });
      } catch {
        if (!cancelled) setLoadFailed(true);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isNewAnalysis, videoId]);

  if (!videoId) {
    return <Navigate to={`${prefix}/events`} replace />;
  }

  if (!isNewAnalysis) {
    const target = `${prefix}/events/${videoId}${
      analysisId ? `/${analysisId}` : ""
    }${location.search}`;
    return <Navigate to={target} state={location.state} replace />;
  }

  if (loadFailed) {
    return <Navigate to={`${prefix}/events`} replace />;
  }

  if (!video) {
    return <AppLoadingScreen />;
  }

  return (
    <Navigate
      to={`${prefix}/use-cases/rapid-review/batch`}
      state={{ videos: [video] }}
      replace
    />
  );
}
